import dayjs from "dayjs";

import { useTimezone } from "@web-app/api";
import { TimeFormat } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";

import Tooltip from "../Tooltip";

export default function ColumnedDateTimeTz({
  date,
  userDate,
  timezone,
  popupPosition = "top",
}: {
  date: Date;
  popupPosition?: "left" | "right" | "top";
  timezone: keyof typeof timeZoneMap;
  userDate: Date;
}) {
  const { userTimezone } = useTimezone();

  if (timezone === "system") {
    return (
      <div className="relative">
        <div className="flex flex-col items-start justify-start cursor-pointer text-sm text-black">
          <FormattedDate date={date} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <Tooltip
        text={<FormattedDate date={userDate} timezone={userTimezone} />}
        position={popupPosition}
        tooltipClassName="w-[125px] flex flex-col"
      >
        <div
          className="flex flex-col items-start justify-start cursor-pointer text-sm text-black"
          role="tooltip"
        >
          <FormattedDate date={date} timezone={timezone} />
        </div>
      </Tooltip>
    </div>
  );
}

const FormattedDate = ({
  date,
  timezone,
}: {
  date: Date;
  timezone?: keyof typeof timeZoneMap;
}) => {
  const { getTimezone } = useTimezone();

  if (!dayjs(date).isValid()) {
    return <span></span>;
  }

  // Convert UTC time to specified timezone for display
  const effectiveTimezone = getTimezone(timezone);
  const timeFormatted =
    effectiveTimezone === "system"
      ? dayjs(date).format(TimeFormat.hour_minute_am_pm)
      : dayjs
          .utc(date)
          .tz(effectiveTimezone)
          .format(TimeFormat.hour_minute_am_pm_tz);

  const dateFormatted =
    effectiveTimezone === "system"
      ? dayjs(date).format(TimeFormat.month_day_year)
      : dayjs.utc(date).tz(effectiveTimezone).format(TimeFormat.month_day_year);

  return (
    <>
      <span>{timeFormatted}</span>
      <span className="text-gray-400">{dateFormatted}</span>
    </>
  );
};
